import React, { useMemo, useState } from "react";
import data from "../assets/mock-data/CarbonPortfolioDashboardData.json";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ComposedChart,
  Scatter,
} from "recharts";
import styles from "../assets/styles/CarbonPortfolioDashboard.module.css";

const green = "#16a34a";
const efficacy = { 10: { N: 0.1, P: 0.22 }, 12: { N: 0.13, P: 0.25 }, 30: { N: 0.36, P: 0.43 } };
const bioKeys = ["Arable", "Buffers", "GS4", "Hedges", "Grass"];

function formatGBP(x) {
  return x == null ? "—" : "£" + x.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function fmt1(x) {
  return x == null ? "—" : x.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

const CarbonPortfolioDashboard = () => {
  const [areaPct, setAreaPct] = useState(10);
  const [mixWillow, setMixWillow] = useState(50);
  // const [bufferW, setBufferW] = useState(12);
  const keys = Object.keys(efficacy).map(Number); // [10, 12, 30]
  const [bufferW, setBufferW] = useState(keys[1]); // default to 12

  const portfolioArea = useMemo(() => data.farms.reduce((s, f) => s + f.area_ha, 0), []);

  const maccByMeasure = useMemo(() => {
    const result = [];
    for (const m of data.macc_measures) {
      let annualCost = 0,
        tco2 = 0;
      for (const f of data.farms) {
        const a = data.applicable_area[f.farm_id]?.[m.measure] || 0;
        annualCost += (m.unit_cost / Math.max(m.lifetime, 1)) * a;
        tco2 += m.annual_co2_per_ha * a;
      }
      result.push({ measure: m.measure, gbp_per_tco2e: tco2 > 0 ? annualCost / tco2 : null });
    }
    return result.sort((a, b) => (a.gbp_per_tco2e ?? 1e9) - (b.gbp_per_tco2e ?? 1e9));
  }, []);

  const scenario = useMemo(() => {
    const tot = (name) => {
      let annualCost = 0,
        co2 = 0;
      for (const m of data.macc_measures) {
        if (m.measure === name) {
          for (const f of data.farms) {
            const a = data.applicable_area[f.farm_id]?.[name] || 0;
            annualCost += (m.unit_cost / Math.max(m.lifetime, 1)) * a;
            co2 += m.annual_co2_per_ha * a;
          }
        }
      }
      return { annualCost, co2 };
    };
    const rip = tot("Riparian buffers 12m");
    const wil = tot("SRC willow (riparian)");
    const mis = tot("Miscanthus (field margin blocks)");
    const scale = areaPct / 10,
      mixW = mixWillow / 100,
      mixM = 1 - mixW;
    const annualised_cost_gbp = rip.annualCost * scale + wil.annualCost * scale * mixW + mis.annualCost * scale * mixM;
    const co2e_t = rip.co2 * scale + wil.co2 * scale * mixW + mis.co2 * scale * mixM;
    const N_kg = 100 * efficacy[bufferW].N * scale;
    const P_kg = 100 * efficacy[bufferW].P * scale;
    return { annualised_cost_gbp, co2e_t, N_kg, P_kg, gbp_per_tco2e: co2e_t > 0 ? annualised_cost_gbp / co2e_t : null };
  }, [areaPct, mixWillow, bufferW]);

  const frontierData = useMemo(() => {
    const base = [...data.frontier];
    base.push({ scenario: "Your scenario", annualised_cost_gbp: scenario.annualised_cost_gbp, co2e_t: scenario.co2e_t });
    return base;
  }, [scenario]);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.card}>
        <div className={`${styles.cardBody} ${styles.about}`}>
          <div className={styles.left}>
            <h1 className={styles.h1}>FarmTwin — Portfolio Dashboard (Merged)</h1>
            <div className={styles.meta}>Version {data.version} · Built {data.build_date} · Codes only</div>
            <div className={styles.meta}>x10AI · ABP · Sainsbury's · FarmTwin Commercial Pilot</div>
          </div>
          <div>
            <span className={styles.tag}>Frontier</span>
            <span className={styles.tag}>MACC</span>
            <span className={styles.tag}>Biodiversity</span>
            <span className={styles.tag}>Risk</span>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className={`${styles.grid} ${styles.row4}`} style={{ marginTop: 16 }}>
        {[
          { label: "Portfolio area", value: `${portfolioArea} ha` },
          { label: "Scenario cost (annualised)", value: formatGBP(Math.round(scenario.annualised_cost_gbp)) },
          { label: "CO₂e sink", value: `${fmt1(scenario.co2e_t)} t/yr` },
          { label: "£/t CO₂e", value: scenario.gbp_per_tco2e ? fmt1(scenario.gbp_per_tco2e) : "—" },
        ].map((kpi, i) => (
          <div className={styles.card} key={i}>
            <div className={`${styles.cardBody} ${styles.kpi}`}>
              <span className={styles.kpiLabel}>{kpi.label}</span>
              <span className={styles.kpiValue}>{kpi.value}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Scenario mixer */}
      <div className={styles.card} style={{ marginTop: 16 }}>
        <div className={styles.cardBody}>
          <h2 className={styles.h2}>Scenario mixer</h2>
          <div className={styles.row}>
            <div>
              <span className={styles.badgeCustom}>Area treated</span>
              <div className={styles.small}>{areaPct}% of portfolio</div>
            </div>
            <input className={styles.sliderCustom} type="range" min="0" max="20" step="1" value={areaPct} onChange={(e) => setAreaPct(parseInt(e.target.value))} />
          </div>
          <div className={styles.row} style={{ marginTop: 8 }}>
            <div>
              <span className={styles.badgeCustom}>Biomass mix</span>
              <div className={styles.small}>{mixWillow}% Willow / {100 - mixWillow}% Miscanthus</div>
            </div>
            <input className={styles.sliderCustom} type="range" min="0" max="100" step="5" value={mixWillow} onChange={(e) => setMixWillow(parseInt(e.target.value))} />
          </div>
          <div className={styles.row} style={{ marginTop: 8 }}>
            <div>
              <span className={styles.badgeCustom}>Buffer width</span>
              <div className={styles.small}>{bufferW} m</div>
            </div>
            <input
              className={styles.sliderCustom}
              type="range"
              min={Math.min(...keys)}
              max={Math.max(...keys)}
              step={1} // step=1, but will snap to closest key
              value={bufferW}
              onChange={(e) => {
                // snap to nearest valid key
                const val = Number(e.target.value);
                const nearest = keys.reduce((a, b) =>
                  Math.abs(b - val) < Math.abs(a - val) ? b : a
                );
                setBufferW(nearest);
              }}
            />
            <div className={styles.small}>Efficacy: N {Math.round(efficacy[bufferW].N * 100)}% · P {Math.round(efficacy[bufferW].P * 100)}%</div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className={`${styles.grid} ${styles.row2}`} style={{ marginTop: 16 }}>
        <div className={styles.card}>
          <div className={styles.cardBody}>
            <h2 className={styles.h2}>Efficient frontier (cost vs CO₂e)</h2>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <ComposedChart data={frontierData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="annualised_cost_gbp" tickFormatter={(v) => "£" + (v / 1000).toFixed(0) + "k"} />
                  <YAxis dataKey="co2e_t" unit=" t" />
                  <Tooltip formatter={(v, n) => n.includes("cost") ? "£" + Math.round(v).toLocaleString() : v} />
                  <Scatter data={data.frontier} fill="#1d4ed8" name="Scenarios" />
                  <Scatter data={[{ annualised_cost_gbp: scenario.annualised_cost_gbp, co2e_t: scenario.co2e_t }]} fill={green} name="Your scenario" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardBody}>
            <h2 className={styles.h2}>MACC — £ per t CO₂e (portfolio)</h2>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={maccByMeasure}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="measure" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="gbp_per_tco2e" />
                  <Tooltip />
                  <Bar dataKey="gbp_per_tco2e" fill={green} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Biodiversity */}
      <div className={styles.card} style={{ marginTop: 16 }}>
        <div className={styles.cardBody}>
          <h2 className={styles.h2}>Biodiversity & habitat composition (by code)</h2>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <ComposedChart data={data.biodiversity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="farm_id" tick={{ fontSize: 12 }} interval={0} />
                <YAxis unit="%" />
                <Tooltip />
                {bioKeys.map((k, i) => (
                  <Bar key={k} dataKey={k} stackId="a" fill={["#6b7280", green, "#f59e0b", "#1d4ed8", "#9ca3af"][i]} />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Risk matrix */}
      <div className={styles.card} style={{ marginTop: 16, marginBottom: 24 }}>
        <div className={styles.cardBody}>
          <h2 className={styles.h2}>Delivery risk matrix (by code)</h2>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.colHead}>Farm Code</th>
                <th className={styles.colHead}>Tenure</th>
                <th className={styles.colHead}>Permissions</th>
                <th className={styles.colHead}>Maintenance</th>
                <th className={styles.colHead}>Data quality</th>
                <th className={styles.colHead}>Funding</th>
                <th className={styles.colHead}>Total</th>
              </tr>
            </thead>
            <tbody>
              {[
                { id: "X10AI_FT_040", risk: [2, 2, 2, 1, 2] },
                { id: "X10AI_FT_020", risk: [3, 2, 3, 2, 3] },
                { id: "X10AI_FT_050", risk: [2, 2, 2, 1, 2] },
                { id: "X10AI_FT_010", risk: [2, 2, 2, 2, 3] },
                { id: "X10AI_FT_060", risk: [3, 3, 3, 3, 3] },
                { id: "X10AI_FT_030", risk: [4, 3, 3, 4, 3] },
              ].map((r) => {
                const total = r.risk.reduce((a, b) => a + b, 0);
                const cells = r.risk.map((v, i) => {
                  const bg = v >= 4 ? "#fee2e2" : v === 3 ? "#fff7ed" : "#ecfdf5";
                  const bd = v >= 4 ? "#fecaca" : v === 3 ? "#ffedd5" : "#bbf7d0";
                  return (
                    <td key={i} style={{ background: bg, border: `1px solid ${bd}` }}>
                      {v}
                    </td>
                  );
                });
                return (
                  <tr key={r.id}>
                    <td>{r.id}</td>
                    {cells}
                    <td>
                      <span className={styles.pill}>{total}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className={styles.small}>1 = low risk, 5 = high risk</div>
        </div>
      </div>
    </div>
  );
};

export default CarbonPortfolioDashboard;
